const PARTICIPATING_STATUS_KEYS = [
  'agreed',
  'billed',
  'billing_not_required',
  'revenue_share_awaiting_event_end',
  'revenue_share_awaiting_reward',
  'revenue_share_billing_available',
];
const SETTLED_STATUS_KEYS = ['billed', 'billing_not_required'];
const INACTIVE_STATUS_KEYS = [
  'billed',
  'billing_not_required',
  'cancelled',
  'cancelled_by_event_delete',
  'no_response',
];

export const PARTICIPATING_CONTRACT_STATUSES = new Set(PARTICIPATING_STATUS_KEYS);
export const SETTLED_CONTRACT_STATUSES = new Set(SETTLED_STATUS_KEYS);
export const INACTIVE_CONTRACT_STATUSES = new Set(INACTIVE_STATUS_KEYS);

const ADMIN_CONTRACT_STATUS_META = {
  pending: { label: '契約待ち', className: 'status-pending' },
  counterproposal: { label: '再契約待ち', className: 'status-counterproposal' },
  cancelled: { label: '取り消し', className: 'status-cancelled' },
  cancelled_by_event_delete: { label: '削除によるキャンセル', className: 'status-cancelled' },
  agreed: { label: '契約済み', className: 'status-agreed' },
  disagreed: { label: '非同意', className: 'status-disagreed' },
  no_response: { label: '未回答（イベント終了）', className: 'status-no-response' },
  billed: { label: '請求済み', className: 'status-billed' },
  billing_not_required: { label: '請求不要', className: 'status-billed' },
  revenue_share_awaiting_event_end: { label: 'イベント終了待ち', className: 'status-agreed' },
  revenue_share_awaiting_reward: { label: '報酬確定待ち', className: 'status-pending' },
  revenue_share_billing_available: { label: '請求申請可能', className: 'status-agreed' },
};

const ARTIST_CONTRACT_STATUS_META = {
  pending: { label: '確認待ち', className: 'status-pending' },
  counterproposal: { label: '差戻し', className: 'status-counterproposal' },
  cancelled: { label: 'キャンセル', className: 'status-cancelled' },
  cancelled_by_event_delete: { label: '削除によるキャンセル', className: 'status-cancelled' },
  agreed: { label: '合意済み', className: 'status-agreed' },
  disagreed: { label: '否認', className: 'status-disagreed' },
  no_response: { label: '未回答', className: 'status-no-response' },
  billed: { label: '請求済み', className: 'status-billed' },
  billing_not_required: { label: '請求不要', className: 'status-billed' },
  revenue_share_awaiting_event_end: { label: 'イベント終了待ち', className: 'status-agreed' },
  revenue_share_awaiting_reward: { label: '報酬確定待ち', className: 'status-pending' },
  revenue_share_billing_available: { label: '請求申請可能', className: 'status-agreed' },
};

const INVOICE_RESUBMISSION_PENDING_META = {
  label: '再発行待ち',
  className: 'status-pending',
};

const SUPPLEMENTAL_INVOICE_PENDING_META = {
  label: '追補請求待ち',
  className: 'status-pending',
  displayKey: 'supplemental_requested',
};

const resolveStatusKey = (status) => String(status || '').toLowerCase();

const normalizeContractLike = (statusOrContract) =>
  typeof statusOrContract === 'object' && statusOrContract !== null
    ? statusOrContract
    : { status: statusOrContract };

const hasRequestedSupplementalInvoice = (statusOrContract) => {
  const contractLike = normalizeContractLike(statusOrContract);
  if (
    contractLike?.has_requested_supplemental_invoice === true ||
    contractLike?.hasRequestedSupplementalInvoice === true
  ) {
    return true;
  }
  const supplementalInvoices =
    contractLike?.supplemental_invoices || contractLike?.supplementalInvoices;
  return Array.isArray(supplementalInvoices)
    ? supplementalInvoices.some(
        (invoice) => resolveStatusKey(invoice?.status) === 'requested'
      )
    : false;
};

export const isInvoiceResubmissionPending = (statusOrContract) => {
  const contractLike = normalizeContractLike(statusOrContract);
  return (
    resolveStatusKey(contractLike?.status) === 'billed' &&
    (contractLike?.invoice_resubmission_requested === true || contractLike?.requested === true)
  );
};

const resolveStatusMeta = (metaMap, statusOrContract) => {
  const contractLike = normalizeContractLike(statusOrContract);
  if (isInvoiceResubmissionPending(contractLike)) {
    return INVOICE_RESUBMISSION_PENDING_META;
  }
  const key = resolveStatusKey(contractLike?.status);
  return metaMap[key] || { label: contractLike?.status || '', className: '' };
};

export const normalizeContractStatus = resolveStatusKey;

export const isParticipatingContractStatus = (status) =>
  PARTICIPATING_CONTRACT_STATUSES.has(resolveStatusKey(status));

export const isSettledContractStatus = (status) =>
  SETTLED_CONTRACT_STATUSES.has(resolveStatusKey(status));

export const isInactiveContractStatus = (status) =>
  INACTIVE_CONTRACT_STATUSES.has(resolveStatusKey(status));

export const getAdminContractStatusMeta = (statusOrContract) =>
  resolveStatusMeta(ADMIN_CONTRACT_STATUS_META, statusOrContract);

export const getAdminContractDisplayStatusMeta = (statusOrContract) => {
  if (hasRequestedSupplementalInvoice(statusOrContract)) {
    return SUPPLEMENTAL_INVOICE_PENDING_META;
  }
  return getAdminContractStatusMeta(statusOrContract);
};

export const getArtistContractStatusMeta = (statusOrContract) =>
  resolveStatusMeta(ARTIST_CONTRACT_STATUS_META, statusOrContract);

export const getAdminContractStatusLabel = (statusOrContract) =>
  getAdminContractStatusMeta(statusOrContract).label;

export const getAdminContractDisplayStatusLabel = (statusOrContract) =>
  getAdminContractDisplayStatusMeta(statusOrContract).label;

export const getArtistContractStatusLabel = (statusOrContract) =>
  getArtistContractStatusMeta(statusOrContract).label;

export const getContractStatusClassName = (statusOrContract, { role = 'admin' } = {}) =>
  (role === 'artist'
    ? getArtistContractStatusMeta(statusOrContract)
    : getAdminContractStatusMeta(statusOrContract))
    .className;
